/* eslint-disable @typescript-eslint/prefer-for-of */

/**
 * Injects a stylesheet into the <head> element if it doesn't already exist.
 * Used for injecting CSS variable definitions by web components and CSS modules by react components.
 *
 * @param {Object} InjectStyleInHeadParams
 * @param {string} InjectStyleInHeadParams.cssText The CSS text to inject.
 * @param {Object<string, string>} InjectStyleInHeadParams.dataset Informative data to attach to the injected stylesheet.
 * @param {boolean} [InjectStyleInHeadParams.insertAtTop] Should the stylesheet be inserted at the top of the <head> element.
 */
export const injectStyleInHead = ({
    cssText,
    dataset,
    insertAtTop,
}: {
    cssText: string;
    dataset: Record<string, string>;
    insertAtTop?: boolean;
}): void => {
    if (cssText && dataset && typeof document !== 'undefined') {
        const doesStyleAlreadyExists = Array.from(document.getElementsByTagName('style')).some(
            (style) => {
                return Object.entries(dataset).every(([key, value]) => {
                    return style.dataset[key] === value;
                });
            },
        );

        if (!doesStyleAlreadyExists) {
            const styleElement = document.createElement('style');

            styleElement.textContent = cssText;
            Object.entries(dataset).forEach(([key, value]) => {
                styleElement.dataset[key] = value;
            });

            const head = document.head;
            head[insertAtTop ? 'prepend' : 'append'](styleElement);

            /* eslint-disable @typescript-eslint/no-non-null-assertion */
            const headObserver = new MutationObserver((mutations) => {
                outer: for (let i = 0; i < mutations.length; i++) {
                    for (let j = 0; j < mutations[i]!.removedNodes.length; j++) {
                        const node = mutations[i]!.removedNodes[j];
                        if (node === styleElement) {
                            head[insertAtTop ? 'prepend' : 'append'](styleElement);
                            break outer; // Breaks out of both loops
                        }
                    }
                }
            });
            /* eslint-enable @typescript-eslint/no-non-null-assertion */
            headObserver.observe(document.head, { childList: true });
        }
    }
};
